import { BooleanInput, coerceBooleanProperty } from '@angular/cdk/coercion';
import {
  ChangeDetectionStrategy,
  Component,
  Input,
  type OnDestroy,
} from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { MatIconButton } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltip } from '@angular/material/tooltip';
import {
  MixedSchema,
  RawInlineNodes,
  initVersionedSchema,
} from '@principle-theorem/editor';
import { PrincipleEditorModule } from '@principle-theorem/ng-interactions';
import {
  DateSelectorComponent,
  OrganisationService,
} from '@principle-theorem/ng-principle-shared';
import {
  MOMENT_DATEPICKER_PROVIDERS,
  NgCommonModule,
  formControlChanges$,
} from '@principle-theorem/ng-shared';
import {
  NgTemplatingModule,
  TemplateEditorInputComponent,
  TemplateEditorTextareaComponent,
} from '@principle-theorem/ng-templating';
import {
  Brand,
  GeneratedTask,
  MOCK_ALL_RESOLVER,
  getScopedConditions,
  stafferToNamedDoc,
} from '@principle-theorem/principle-core';
import {
  AutomationContext,
  ConditionLogicConfigurationCollection,
  ConditionLogicId,
  IAutomationConfiguration,
  IAutomationTiming,
  IGeneratedTask,
  IPractice,
  ITemplateContext,
  ITemplateScopeData,
  TASK_PRIORITIES,
  TemplateScope,
  TemplateType,
  isGeneratedTaskConfiguration,
  isStaffer,
  type IGeneratedTaskConfiguration,
  type IStaffer,
  type ITeam,
  type TaskPriority,
} from '@principle-theorem/principle-core/interfaces';
import {
  DocumentReference,
  Firestore,
  TIME_FORMAT_24HR,
  Timestamp,
  filterUndefined,
  isChanged$,
  isSameRef,
  mergeDayAndTime,
  toMoment,
  toNamedDocument,
  toTimestamp,
  type INamedDocument,
  type WithRef,
} from '@principle-theorem/shared';
import * as moment from 'moment-timezone';
import { duration, type Moment } from 'moment-timezone';
import {
  BehaviorSubject,
  ReplaySubject,
  Subject,
  combineLatest,
  of,
  type Observable,
} from 'rxjs';
import { filter, map, switchMap, take, takeUntil } from 'rxjs/operators';
import { AUTOMATION_CONDITIONS_HELP_TEXT } from '../automated-notification-configuration-edit/automated-notification-configuration-form';
import { AutomationTimingFormComponent } from '../automation-timing-form/automation-timing-form.component';
import { AutomationTreatmentsComponent } from '../automation-treatments/automation-treatments.component';
import { IGeneratedTaskReturnData } from '../generated-task-dialog/generated-task-dialog.component';
import { AutomatedNotificationFormHelpers } from '../notification-dialog/notification-dialog-form';
import { GeneratedTaskEditForm } from './generated-task-edit-form';

export interface IGeneratedTaskFormData extends IGeneratedTaskConfiguration {
  assignee: INamedDocument<IStaffer | ITeam>;
  triggerDate?: Moment;
  triggerTime?: string;
}

@Component({
  selector: 'pr-generated-task-edit',
  templateUrl: './generated-task-edit.component.html',
  styleUrls: ['./generated-task-edit.component.scss'],
  exportAs: 'prGeneratedTaskEdit',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [...MOMENT_DATEPICKER_PROVIDERS],
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatDatepickerModule,
    MatSelectModule,
    MatIconButton,
    MatIcon,
    MatTooltip,
    NgCommonModule,
    NgTemplatingModule,
    PrincipleEditorModule,
    DateSelectorComponent,
    AutomationTimingFormComponent,
    AutomationTreatmentsComponent,
    TemplateEditorTextareaComponent,
    TemplateEditorInputComponent,
  ],
})
export class GeneratedTaskEditComponent implements OnDestroy {
  private _onDestroy$ = new Subject<void>();
  formHelpers = AutomatedNotificationFormHelpers;
  templateContext$: Observable<ITemplateContext>;
  config$: Observable<WithRef<IGeneratedTaskConfiguration> | undefined>;
  form = new GeneratedTaskEditForm();
  teams$: Observable<WithRef<ITeam>[]>;
  staff$: Observable<WithRef<IStaffer>[]>;
  taskPriorities: TaskPriority[] = TASK_PRIORITIES;
  timeInterval = duration(15, 'minutes');
  taskConfig?: IGeneratedTaskConfiguration | undefined;
  noScope: boolean;
  hasScope$: Observable<boolean>;
  availableConditions$: Observable<ConditionLogicId[]>;
  defaultCondition = ConditionLogicId.Always;
  automationConditionsHelp = AUTOMATION_CONDITIONS_HELP_TEXT;
  htmlTemplateType = TemplateType.Html;

  practices$ = new ReplaySubject<INamedDocument<IPractice>[]>(1);
  task$ = new BehaviorSubject<IGeneratedTask | undefined>(undefined);
  useRelativeTime$ = new BehaviorSubject<boolean>(false);
  scope$ = new ReplaySubject<TemplateScope>(1);
  scopeData$ = new ReplaySubject<ITemplateScopeData>(1);
  triggerDate$ = new BehaviorSubject<Timestamp | undefined>(undefined);
  configRef$ = new BehaviorSubject<
    DocumentReference<IAutomationConfiguration> | undefined
  >(undefined);
  context$ = new ReplaySubject<AutomationContext>(1);
  isAppointmentContext$ = this.context$.pipe(
    map((context) => context === AutomationContext.Appointment)
  );
  isMarketingContext$ = this.context$.pipe(
    map((context) => context === AutomationContext.Marketing)
  );

  @Input()
  set context(context: AutomationContext) {
    if (context) {
      this.context$.next(context);
    }
  }

  @Input()
  set configRef(
    configRef: DocumentReference<IAutomationConfiguration> | undefined
  ) {
    if (configRef) {
      this.configRef$.next(configRef);
    }
  }

  @Input()
  set triggerDate(triggerDate: Timestamp | undefined) {
    if (triggerDate) {
      this.triggerDate$.next(triggerDate);
    }
  }

  @Input()
  set scopeData(scopeData: ITemplateScopeData) {
    if (scopeData) {
      this.scopeData$.next(scopeData);
    }
  }

  @Input()
  set scope(scope: TemplateScope) {
    if (scope) {
      this.scope$.next(scope);
    }
  }

  @Input()
  set practices(practices: INamedDocument<IPractice>[] | undefined) {
    if (practices) {
      this.practices$.next(practices);
      this.form.controls.practiceRef.enable();
    }
  }

  @Input()
  set useRelativeTime(useRelativeTime: BooleanInput) {
    this.useRelativeTime$.next(coerceBooleanProperty(useRelativeTime));
  }

  @Input()
  set task(task: IGeneratedTask | undefined) {
    if (task) {
      this.task$.next(task);
    }
  }

  constructor(private _organisation: OrganisationService) {
    this.task$
      .pipe(filterUndefined(), take(1), takeUntil(this._onDestroy$))
      .subscribe((task) => this.form.patchValue(task));

    combineLatest([this.triggerDate$, this.useRelativeTime$])
      .pipe(
        filter(
          ([triggerDate, useRelativeTime]) => !!triggerDate && !useRelativeTime
        ),
        takeUntil(this._onDestroy$)
      )
      .subscribe(([triggerDate]) => {
        if (!triggerDate) {
          return;
        }
        this.form.enableTriggerControls();
        this.form.controls.triggerDate.setValue(toMoment(triggerDate));
        this.form.controls.triggerTime.setValue(
          toMoment(triggerDate).format(TIME_FORMAT_24HR)
        );
      });

    this.staff$ = this._organisation.staff$;

    this.teams$ = this._organisation.brand$.pipe(
      switchMap((brand) => (brand ? Brand.teams$(brand) : of([])))
    );

    this.config$ = this.configRef$.pipe(
      switchMap((configRef) => this._getConfig(configRef))
    );

    const resolvedScope$ = combineLatest([
      formControlChanges$(this.form.controls.scope),
      this.scope$,
    ]).pipe(map(([templateScope, scope]) => templateScope ?? scope));

    this.templateContext$ = resolvedScope$.pipe(
      isChanged$(),
      switchMap(async (scope) => MOCK_ALL_RESOLVER.resolve([scope]))
    );

    this.hasScope$ = this.scope$.pipe(
      map((scope) => scope !== TemplateScope.None)
    );

    this.availableConditions$ = this.scope$.pipe(
      map((scope) =>
        getScopedConditions(scope).filter(
          (condition) => condition !== ConditionLogicId.Never
        )
      )
    );
  }

  ngOnDestroy(): void {
    this._onDestroy$.next();
    this._onDestroy$.complete();
  }

  clearTriggerDate(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.form.controls.triggerDate.reset();
  }

  requiredConditionsChange(event: ConditionLogicConfigurationCollection): void {
    this.form.controls.requiredConditions.setValue(event);
  }

  timingChange(event: IAutomationTiming): void {
    this.form.controls.timing.setValue({
      ...event,
      // eslint-disable-next-line no-null/no-null
      time: event.time ?? null,
    });
  }

  compareFn(value: INamedDocument, assignee: INamedDocument): boolean {
    if (!value || !assignee) {
      return false;
    }

    return isSameRef(value, assignee);
  }

  submit(): IGeneratedTaskReturnData | undefined {
    if (!this.form.valid) {
      return;
    }

    const formValue = this.form.getRawValue();
    formValue.isForTeam = !isStaffer(formValue.assignee);
    formValue.assignee = isStaffer(formValue.assignee)
      ? stafferToNamedDoc(formValue.assignee)
      : toNamedDocument(formValue.assignee);
    const task = GeneratedTask.init(formValue);

    const result: IGeneratedTaskReturnData = {
      ...task,
      assignee: formValue.assignee,
    };

    if (formValue.triggerDate && formValue.triggerTime) {
      const triggerDate = mergeDayAndTime(
        toMoment(formValue.triggerDate),
        moment(formValue.triggerTime, TIME_FORMAT_24HR)
      );
      result.triggerDate = toTimestamp(triggerDate);
    }

    return result;
  }

  toMixedSchema(value?: RawInlineNodes): MixedSchema {
    return initVersionedSchema(value);
  }

  private async _getConfig(
    configRef?: DocumentReference<IAutomationConfiguration>
  ): Promise<WithRef<IGeneratedTaskConfiguration> | undefined> {
    if (!configRef) {
      return undefined;
    }
    const config = await Firestore.getDoc(configRef);
    if (!isGeneratedTaskConfiguration(config)) {
      // eslint-disable-next-line no-console
      console.warn(`Expected Generated Task Configuration: ${config.ref.path}`);
      return undefined;
    }
    return config as WithRef<IGeneratedTaskConfiguration>;
  }
}
